import styled from 'styled-components';

import { useCallback } from 'react';
import { useQuoteBuilderActions } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/form-context';
import { ValueSelectorsProvider } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/value-selector-context';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/view';
import { firefly, razzmatazz, lima } from '@paradigm/design-system/src/colors';
import { toast } from '#/components/UnifiedRfqs/toast';
import { TableQuoteItem } from '@paradigm/features/src/unified-rfqs/ui/types';
import { track } from '@paradigm/analytics/src/analytics';
import { useGrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { getProduct } from '@paradigm/features/src/products/domain';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useIsMultiLegStrategy } from '@paradigm/features/src/unified-rfqs/ui/hooks/shared';
import { useBboView } from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import { useTrackAction } from '@paradigm/features/src/analytics';
import QuotesTable from '../../components/QuotesTable';
import QuotesRows, { ColorCtx } from '../../components/QuotesRows';
import ScreenBestField from '../../components/ScreenBestField';

export default function BidsAndAsksTables() {
  return (
    <TableContainer>
      <QuotesTableSided key="BUY" side="BUY" />
      <QuotesTableSided key="SELL" side="SELL" />
    </TableContainer>
  );
}

export interface QuotesTableSidedArgs {
  readonly side: 'BUY' | 'SELL';
  readonly color?: string;
}

function QuotesTableSided({
  side,
  color = side === 'BUY' ? lima : razzmatazz,
}: QuotesTableSidedArgs) {
  const { quotesTable, bestQuote, legs } = useQuoteBuilderView();

  const {
    changePrice,
    loadLegPricesFromQuote,
    changeLegPricesFromBbo,
    changeQuantity,
  } = useQuoteBuilderActions();
  const rfq = useGrfqRfq();

  const isOffer = side === 'SELL';
  const { quotes, myQuotes } = quotesTable[isOffer ? 'asks' : 'bids'];
  const { price, quantity } = bestQuote[isOffer ? 'ask' : 'bid'];
  const sideLabel = isOffer ? 'Ask' : 'Bid';

  const { quoteCurrency } = getProduct(rfq.product_codes[0]!);
  const isDated = isDatedRfqLegs(rfq.legs, rfq.product_codes[0]);

  const isMultiLegStrategy = useIsMultiLegStrategy(rfq);

  const loadPrices = loadLegPricesFromQuote;

  const trackAction = useTrackAction();

  const selectPrice = useCallback(
    (quote: Pick<TableQuoteItem, 'price'>) => {
      trackAction('select orderbook price', { side });
      // remove old tracking code (https://tradeparadigm.atlassian.net/browse/PTP-20626)
      track(`grfqs__${sideLabel}__clicked`, { priceValue: quote.price });
      if (isMultiLegStrategy)
        loadPrices({ price: quote.price, side, onError: toast });
      else changePrice(quote.price);
    },
    [changePrice, isMultiLegStrategy, loadPrices, side, sideLabel, trackAction],
  );

  const selectBestPrice = useCallback(
    (quote: Pick<TableQuoteItem, 'price'>) => {
      trackAction('select screen best price', { side });
      // remove old tracking code (https://tradeparadigm.atlassian.net/browse/PTP-20626)
      track(`grfqs__best${sideLabel}__clicked`, { priceValue: quote.price });
      changePrice(quote.price);
      if (isMultiLegStrategy) changeLegPricesFromBbo(side);
    },
    [
      changeLegPricesFromBbo,
      changePrice,
      isMultiLegStrategy,
      side,
      sideLabel,
      trackAction,
    ],
  );

  const selectQuantity = useCallback(
    (quote: Pick<TableQuoteItem, 'quantity'>) => {
      trackAction('select orderbook quantity', { side });
      const formattedQty = formatProductQty(
        quote.quantity,
        rfq.product_codes[0],
        false,
        isDated,
      );
      // remove old tracking code (https://tradeparadigm.atlassian.net/browse/PTP-20626)
      track(`grfqs__${sideLabel}quantity__clicked`, { quantity: formattedQty });
      changeQuantity(formattedQty);
    },
    [changeQuantity, isDated, rfq.product_codes, side, sideLabel, trackAction],
  );

  const selectBestQuantity = useCallback(
    (quote: Pick<TableQuoteItem, 'quantity'>) => {
      trackAction('select screen best quantity', { side });
      const formattedQty = formatProductQty(
        quote.quantity,
        rfq.product_codes[0],
        false,
        isDated,
      );
      // remove old tracking code (https://tradeparadigm.atlassian.net/browse/PTP-20626)
      track(`grfqs__best${sideLabel}quantity__clicked`, {
        quantity: formattedQty,
      });
      changeQuantity(formattedQty);
    },
    [changeQuantity, isDated, rfq.product_codes, side, sideLabel, trackAction],
  );

  const { bestAskIv, bestBidIv } = useBboView();

  const screenBestIV = isOffer ? bestAskIv.formatted : bestBidIv.formatted;

  return (
    <ColorCtx.Provider value={color}>
      <div>
        <QuotesTable
          isOffer={isOffer}
          hasOwnQuotes={myQuotes.length > 0}
          contractCurrency={legs[0]?.contractCurrency}
          quoteCurrency={quoteCurrency}
        >
          <ValueSelectorsProvider value={{ selectPrice, selectQuantity }}>
            <QuotesRows quotes={quotes} myQuotes={myQuotes} side={side} />
          </ValueSelectorsProvider>
        </QuotesTable>

        <ValueSelectorsProvider
          value={{
            selectPrice: selectBestPrice,
            selectQuantity: selectBestQuantity,
          }}
        >
          <ScreenBestField
            price={price}
            quantity={quantity}
            sideLabel={sideLabel}
            side={side}
            ivBestPrice={screenBestIV}
          />
        </ValueSelectorsProvider>
      </div>
    </ColorCtx.Provider>
  );
}

const TableContainer = styled.div`
  background-color: ${firefly};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 1rem;
  column-gap: 1rem;
  border-radius: 10px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    overflow-x: scroll;
    column-gap: 0.75rem;
    padding: 0.75rem;
    border-radius: 3px;
  }
`;
